import { useEffect, useState } from "react";
import { Button, Input } from "antd";
import { EditOutlined } from "@ant-design/icons";
import { apiClient } from "@api/apiClient";
import { useNotificationContext } from "@hooks/useNotificationContext";
import styles from "./styles.module.less";

interface AliasNameEditorProps {
  conversationId?: string;
  conversationName?: string;
  alias?: string;
  isZalo?: boolean;
  badge?: React.ReactNode;
  onAliasUpdated?: (alias: string) => void;
}

export const AliasNameEditor = ({
  conversationId,
  conversationName,
  alias,
  isZalo,
  badge,
  onAliasUpdated,
}: AliasNameEditorProps) => {
  const { success, error: showError } = useNotificationContext();
  const [isEditing, setIsEditing] = useState(false);
  const [aliasInput, setAliasInput] = useState("");
  const [aliasName, setAliasName] = useState("");
  const [savingAlias, setSavingAlias] = useState(false);

  useEffect(() => {
    const currentAlias = alias || conversationName || "";
    setAliasName(currentAlias);
    setAliasInput(currentAlias);
    setIsEditing(false);
  }, [conversationId, alias, conversationName]);

  const handleCancel = () => {
    setIsEditing(false);
    setAliasInput(aliasName);
  };

  const handleSave = async () => {
    if (!conversationId || !isZalo) return;
    const newName = aliasInput.trim();
    const currentName = aliasName.trim();

    if (!newName || newName === currentName) {
      setIsEditing(false);
      return;
    }

    try {
      setSavingAlias(true);
      await apiClient.put(`/api/zalo/internal/conversations/${conversationId}/alias`, {
        alias: newName,
      });
      setAliasName(newName);
      setIsEditing(false);
      success("Cập nhật tên thành công");
      onAliasUpdated?.(newName);
    } catch (err) {
      showError(err instanceof Error ? err.message : "Cập nhật tên thất bại");
    } finally {
      setSavingAlias(false);
    }
  };

  const displayName = aliasName || conversationName || "-";
  const canEdit = Boolean(isZalo && conversationId);

  return (
    <div className={styles.container}>
      {!isEditing ? (
        <div className={styles.displayRow}>
          <span className={styles.nameText}>{displayName}</span>
          {badge}
          {canEdit && (
            <Button
              type="text"
              icon={<EditOutlined />}
              className={styles.editButton}
              onClick={() => {
                setAliasInput(displayName);
                setIsEditing(true);
              }}
              aria-label="Sửa tên gợi nhớ"
            />
          )}
        </div>
      ) : (
        <div className={styles.editRow}>
          <Input
            value={aliasInput}
            onChange={(e) => setAliasInput(e.target.value)}
            maxLength={250}
            onKeyDown={(e) => {
              if (e.key === "Enter") handleSave();
              if (e.key === "Escape") handleCancel();
            }}
            autoFocus
            size="small"
            className={styles.aliasInput}
          />
          <span className={styles.aliasCount}>{aliasInput.length}/250</span>
          <Button size="small" onClick={handleCancel}>
            Hủy
          </Button>
          <Button
            type="primary"
            size="small"
            onClick={handleSave}
            disabled={
              !aliasInput.trim() || aliasInput.trim() === aliasName.trim()
            }
            loading={savingAlias}
          >
            Lưu
          </Button>
        </div>
      )}
    </div>
  );
};
